import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { forEach, groupBy } from 'lodash';
import moment from 'moment';
import { useGraphColors } from '@hooks/useGraphColors';

interface IChartDataItem {
  date: string;
  value: number;
  category: string;
  type: string;
}

interface ChartDatum {
  category?: string;
  [key: string]: unknown;
}

interface TextAnnotation {
  type: 'text';
  position: [string, number];
  style: {
    textAlign: 'center';
    fontSize: number;
    fill: string;
  };
  content: string;
  offsetX?: number;
  rotate?: number;
  offsetY: number;
  tooltip: false;
}

export interface IChartDataResponse {
  time: string;
  customerId: number;
  dataInicio: string;
  dataTermino: string;
  dia: number;
  mes: number;
  ano: number;
  produto: string;
  valorFatura: number;
  faturaId: number;
}

type InvoiceType = 'closed' | 'open';

interface UseBillingChartParams {
  data: IChartDataResponse[] | undefined;
  isLoading: boolean;
  invoiceType: InvoiceType;
  dateFormat: string;
  theme: { colors?: { text?: string } } | null;
  graphThemeKey: string;
  formatCurrency: (value: number) => string;
}

export function useBillingChart({
  data,
  isLoading,
  invoiceType,
  dateFormat,
  theme,
  graphThemeKey,
  formatCurrency,
}: UseBillingChartParams) {
  const { t } = useTranslation(['monthly-billing']);
  const graphColors = useGraphColors();

  const [chartData, setChartData] = useState<IChartDataItem[]>([]);
  const [originalProducts, setOriginalProducts] = useState<string[]>([]);
  const [visibleCategories, setVisibleCategories] = useState<Set<string>>(
    new Set(),
  );

  const typeLabel =
    invoiceType === 'closed'
      ? t('monthly-billing:invoice-closed')
      : t('monthly-billing:invoice-opened');

  useEffect(() => {
    const products = new Set<string>();

    data?.forEach(product => {
      if (product.produto && product.produto !== 'nan') {
        products.add(product.produto);
      }
    });

    const productsArray = Array.from(products);
    if (productsArray.length && originalProducts.length === 0) {
      setOriginalProducts(productsArray);
    }
  }, [data, originalProducts.length]);

  useEffect(() => {
    if (!isLoading && data?.length) {
      const processedData: IChartDataItem[] = [];

      data.forEach(product => {
        const produto = product?.produto;

        if (produto && produto !== 'nan') {
          processedData.push({
            date:
              invoiceType === 'closed'
                ? moment(product?.time).format(dateFormat)
                : moment.utc(product?.time as string).format(dateFormat),
            value: Number(product?.valorFatura) || 0,
            category: `${produto} (${typeLabel})`,
            type: invoiceType,
          });
        }
      });

      setChartData(processedData);
    }
  }, [data, isLoading, invoiceType, dateFormat, typeLabel]);

  useEffect(() => {
    if (chartData.length > 0) {
      const allCategories = [...new Set(chartData.map(item => item.category))];
      setVisibleCategories(new Set(allCategories));
    }
  }, [chartData]);

  const allCategories = useMemo(() => {
    return [...new Set(chartData.map(item => item.category))];
  }, [chartData]);

  const groupedCategories = useMemo(() => {
    const closedLabel = t('monthly-billing:invoice-closed');
    const openedLabel = t('monthly-billing:invoice-opened');

    const productGroups: Record<string, string[]> = {};

    allCategories.forEach(category => {
      const productName = category
        .replace(` (${closedLabel})`, '')
        .replace(` (${openedLabel})`, '');

      if (!productGroups[productName]) {
        productGroups[productName] = [];
      }
      productGroups[productName].push(category);
    });

    Object.keys(productGroups).forEach(productName => {
      productGroups[productName].sort((a, b) => {
        const aIsClosed = a.includes(`(${closedLabel})`);
        const bIsClosed = b.includes(`(${closedLabel})`);

        if (aIsClosed && !bIsClosed) return -1;
        if (!aIsClosed && bIsClosed) return 1;
        return 0;
      });
    });

    const result: string[] = [];
    Object.keys(productGroups)
      .sort()
      .forEach(productName => {
        result.push(...productGroups[productName]);
      });

    return result;
  }, [allCategories, t]);

  const sortDataByDate = useCallback(
    (items: IChartDataItem[]): IChartDataItem[] => {
      return [...items].sort((a, b) => {
        const partsA = a.date.split('/').map(Number);
        const partsB = b.date.split('/').map(Number);

        if (partsA.length === 2 && partsB.length === 2) {
          const [monthA, yearA] = partsA;
          const [monthB, yearB] = partsB;

          if (yearA !== yearB) {
            return yearA - yearB;
          }
          return monthA - monthB;
        }

        const [dayA, monthA] = partsA;
        const [dayB, monthB] = partsB;

        if (monthA !== monthB) {
          return monthA - monthB;
        }
        return dayA - dayB;
      });
    },
    [],
  );

  const filteredChartData = useMemo(() => {
    const filtered = chartData.filter(item =>
      visibleCategories.has(item.category),
    );
    return sortDataByDate(filtered);
  }, [chartData, visibleCategories, sortDataByDate]);

  const uniqueProducts = useMemo(() => {
    const products = new Set<string>();
    const closedLabel = t('monthly-billing:invoice-closed');
    const openedLabel = t('monthly-billing:invoice-opened');

    chartData.forEach(item => {
      const productName = item.category
        .replace(` (${openedLabel})`, '')
        .replace(` (${closedLabel})`, '');
      products.add(productName);
    });

    return Array.from(products);
  }, [chartData, t]);

  const getChartColor = useCallback(
    (datum: ChartDatum) => {
      const productColors =
        graphColors[graphThemeKey as keyof typeof graphColors];

      if (!datum || !datum.category) {
        return productColors[0];
      }

      const closedLabel = t('monthly-billing:invoice-closed');
      const openedLabel = t('monthly-billing:invoice-opened');

      const productName = datum.category
        .toString()
        .replace(` (${closedLabel})`, '')
        .replace(` (${openedLabel})`, '');

      const productIndex = uniqueProducts.indexOf(productName);
      const baseColor = productColors[productIndex % productColors.length];

      if (datum.category.toString().includes(`(${openedLabel})`)) {
        return `${baseColor}80`;
      }

      return baseColor;
    },
    [graphColors, graphThemeKey, t, uniqueProducts],
  );

  const getCategoryColor = useCallback(
    (category: string) => {
      return getChartColor({ category });
    },
    [getChartColor],
  );

  const chartAnnotations = useMemo(() => {
    const annotations: TextAnnotation[] = [];
    forEach(groupBy(filteredChartData, 'date'), (values, date) => {
      const totalValue = values.reduce(
        (sum, item) => sum + (item.value as number),
        0,
      );

      if (totalValue > 0) {
        const isOpenInvoice = invoiceType === 'open';

        annotations.push({
          type: 'text',
          position: [date, totalValue],
          style: {
            textAlign: 'center',
            fontSize: 12,
            fill: theme?.colors?.text || 'rgba(0,0,0,0.85)',
          },
          content: formatCurrency(totalValue),
          offsetX: isOpenInvoice ? -8 : 0,
          offsetY: -10,
          rotate: isOpenInvoice ? -Math.PI / 4 : 0,
          tooltip: false,
        });
      }
    });

    return annotations;
  }, [filteredChartData, theme, formatCurrency, invoiceType]);

  const handleCategoryToggle = useCallback((category: string) => {
    setVisibleCategories(prev => {
      const newVisible = new Set(prev);
      if (newVisible.has(category)) {
        newVisible.delete(category);
      } else {
        newVisible.add(category);
      }
      return newVisible;
    });
  }, []);

  const handleCategoryIsolate = useCallback(
    (category: string) => {
      if (visibleCategories.size === 1 && visibleCategories.has(category)) {
        setVisibleCategories(new Set(allCategories));
      } else {
        setVisibleCategories(new Set([category]));
      }
    },
    [visibleCategories, allCategories],
  );

  const hasData = chartData.length > 0;

  return {
    chartData,
    filteredChartData,
    hasData,
    groupedCategories,
    visibleCategories,
    chartAnnotations,
    getChartColor,
    getCategoryColor,
    handleCategoryToggle,
    handleCategoryIsolate,
  };
}
